import { useCallback, useEffect, useRef, useState } from 'react';

import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { kstDayEndISO, kstDayStartISO } from '@shared/utils/kstTime.util';

import { BugoStatFevent } from './adminBugoStat.types';
import { useAnalyticsBrandNames } from './useAnalyticsBrandNames';

/**
 * 한 번에 끌어올 상가 수 상한.
 * 전용 집계 API가 없어 프론트에서 집계하므로(스펙: docs/admin-bugo-stat-spec.md),
 * 응답이 지나치게 커지지 않도록 자른다. 잘린 경우 화면에 경고를 띄운다.
 */
export const FEVENT_FETCH_LIMIT = 20000;

/**
 * 조회 가능한 최대 기간.
 * 반기(약 184일)까지 한 번에 보려고 열어 뒀다 — 실제 안전장치는 이 일수가 아니라
 * FEVENT_FETCH_LIMIT(문서 수)이고, 넘치면 화면에 "일부만 집계" 경고가 뜬다.
 */
export const MAX_RANGE_DAYS = 200;

//* 집계 + 자세히 보기 목록에 필요한 필드만.
//* populate가 걸린 bugoBrand·funeralHomeInfo는 select에 반드시 포함해야 한다.
const FEVENT_SELECT = [
  'bugoBrand',
  'bugoBrandAgencyIndex',
  'useAcqMsg',
  'registerNumber',
  'meta.shareCount',
  'meta.isDeleted',
  'deceasedInfo.name',
  'deceasedInfo.coffinOut.date',
  'funeralHomeInfo',
  'funeralHomeInfoEmbed.name',
  'roomInfoEmbed.name',
  'createdAt',
].join(' ');

//* 상조명은 이름 맵(`useAnalyticsBrandNames`)으로 붙인다 — populate하면 bugoAgencyList가
//* 문서마다 통째로 복사돼 응답이 부푼다. 맵을 못 받았을 때만 폴백으로 붙인다.
const BRAND_POPULATE = {
  path: 'bugoBrand',
  select:
    'type bugoAgency.corpName bugoAgency.nickName bugoAgencyList.corpName bugoAgencyList.nickName',
};

const FUNERAL_HOME_POPULATE = { path: 'funeralHomeInfo', select: 'name' };

const BUGO_POPULATE = { path: 'bugos', select: 'lastSentPhoneNumber' };

//* 조의글은 개수만 세므로 isBan만 받는다 (_id는 항상 따라온다) — 본문까지 받으면 응답이 폭발한다
const ACQ_MSG_POPULATE = { path: 'acqMsgs', select: 'isBan' };

/** virtual populate를 어디까지 붙여볼지 — 실패하면 한 단계씩 떨어뜨린다 */
type PopulateLevel = 'full' | 'bugoOnly' | 'none';

const populateOf = (level: PopulateLevel, brandPopulate: boolean) => {
  const base = brandPopulate
    ? [BRAND_POPULATE, FUNERAL_HOME_POPULATE]
    : [FUNERAL_HOME_POPULATE];
  if (level === 'full') return [...base, BUGO_POPULATE, ACQ_MSG_POPULATE];
  if (level === 'bugoOnly') return [...base, BUGO_POPULATE];
  return base;
};

const buildUrl = (
  from: string,
  to: string,
  level: PopulateLevel,
  brandPopulate: boolean,
): string => {
  const query = queryString({
    filter: {
      //* 발인일 기준. isDeleted는 절대 필터하지 않는다 (과거 기간이 통째로 비어 보인다)
      'deceasedInfo.coffinOut.date': {
        $gte: kstDayStartISO(from),
        $lte: kstDayEndISO(to),
      },
    },
    populate: populateOf(level, brandPopulate),
    options: {
      limit: FEVENT_FETCH_LIMIT,
      sort: { 'deceasedInfo.coffinOut.date': 1 },
    },
  });
  //* queryString은 encode:false라 공백이 든 select를 직접 인코딩해야 URL이 깨지지 않는다
  return `admin/fevent?${query}&select=${encodeURIComponent(FEVENT_SELECT)}`;
};

export interface AdminBugoStatState {
  status: 'idle' | 'loading' | 'success' | 'error';
  fevents: BugoStatFevent[];
  /** bugos populate가 거부돼 발송 수치를 못 쓰는 상태 (구서버 StrictPopulate 등) */
  bugoDataUnavailable: boolean;
  /** acqMsgs populate가 거부돼 조의글 개수를 못 쓰는 상태 */
  acqMsgDataUnavailable: boolean;
  /** FEVENT_FETCH_LIMIT에 걸려 잘렸는지 */
  truncated: boolean;
  errorMessage: string | null;
}

const INITIAL: AdminBugoStatState = {
  status: 'idle',
  fevents: [],
  bugoDataUnavailable: false,
  acqMsgDataUnavailable: false,
  truncated: false,
  errorMessage: null,
};

const errorMessageOf = (err: unknown): string => {
  const res = (err as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  //* 백엔드 전역 필터가 error 코드를 덮어써서 400에서만 서버 message가 의미 있다
  if (res?.status === 400 && res.data?.message) return res.data.message;
  if (res?.status === 401 || res?.status === 403) return '조회 권한이 없습니다.';
  return '통계를 불러오지 못했습니다.';
};

/**
 * 발인일 기준 상가 원자료 조회.
 * 전용 집계 엔드포인트가 없어 /admin/fevent 제네릭 조회로 받아 프론트에서 집계한다.
 *
 * bugos·acqMsgs는 virtual populate라 구서버가 StrictPopulate로 막을 수 있다.
 * 하나가 막혔다고 둘 다 버리지 않도록 full → bugoOnly → none 순으로 한 단계씩
 * 떨어뜨리고, 못 받은 쪽 수치만 '집계 불가'로 표시한다.
 */
export const useAdminBugoStat = (from: string, to: string) => {
  const [state, setState] = useState<AdminBugoStatState>(INITIAL);
  //* 늦게 도착한 이전 요청이 최신 결과를 덮어쓰지 않도록
  const requestIdRef = useRef(0);
  //* 이름 맵이 정해져야 brand populate를 붙일지 말지가 정해진다 (세션 캐시라 보통 즉시)
  const { status: brandStatus, resolveBrandName, brandPopulateFallback } =
    useAnalyticsBrandNames();

  const fetchStat = useCallback(async () => {
    if (!from || !to || from > to) return;
    if (brandStatus === 'loading') return;
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, status: 'loading', errorMessage: null }));

    const load = async (level: PopulateLevel) => {
      const { data } = await api_.get<BugoStatFevent[]>(
        buildUrl(from, to, level, brandPopulateFallback),
      );
      return Array.isArray(data) ? data : [];
    };

    //* StrictPopulate는 500으로 떨어진다. 그 외 오류(권한·네트워크)는 그대로 올린다
    const isPopulateRejection = (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status === 500 || status === 400;
    };

    try {
      const levels: PopulateLevel[] = ['full', 'bugoOnly', 'none'];
      let fevents: BugoStatFevent[] | null = null;
      let usedLevel: PopulateLevel = 'none';

      for (let i = 0; i < levels.length; i += 1) {
        const level = levels[i] as PopulateLevel;
        try {
          fevents = await load(level);
          usedLevel = level;
          break;
        } catch (err) {
          //* 마지막 단계까지 실패했거나 populate 문제가 아니면 그대로 실패 처리
          if (i === levels.length - 1 || !isPopulateRejection(err)) throw err;
        }
      }

      if (requestIdRef.current !== requestId) return;
      const rows = fevents ?? [];
      setState({
        status: 'success',
        fevents: rows,
        bugoDataUnavailable: usedLevel === 'none',
        acqMsgDataUnavailable: usedLevel !== 'full',
        truncated: rows.length >= FEVENT_FETCH_LIMIT,
        errorMessage: null,
      });
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setState({
        ...INITIAL,
        status: 'error',
        errorMessage: errorMessageOf(err),
      });
    }
  }, [from, to, brandStatus, brandPopulateFallback]);

  useEffect(() => {
    void fetchStat();
  }, [fetchStat]);

  return { ...state, resolveBrandName, refetch: fetchStat };
};
